import { Box, Button, Typography } from '@mui/material';
import forbiddenAccessImage from '@parkdna/ui/assets/forbidden-access-image.png';
import { useEffect, useState } from 'react';

import { useAuth } from '@/hooks/auth/useAuth';

const SIGN_OUT_AFTER_IN_MS = 10000;
const SIGN_OUT_TARGET_STORAGE_KEY = 'app-signOutTargetTime';

export type ForbiddenScreenProps = {
  autoLogout?: boolean;
};

export default function ForbiddenScreen(
  { autoLogout }: ForbiddenScreenProps = { autoLogout: false }
) {
  const { orgName, signOut } = useAuth();

  const [remainingTimeMs, setRemainingTimeMs] = useState(() => {
    if (!autoLogout) {
      return Infinity;
    }

    const storedTarget = localStorage.getItem(SIGN_OUT_TARGET_STORAGE_KEY);
    if (storedTarget) {
      const targetTime = parseInt(storedTarget, 10);
      if (Date.now() >= targetTime) {
        void signOut();
        return 0;
      }
      return targetTime - Date.now();
    }

    // No target stored – set a new target time.
    const newTargetTime = Date.now() + SIGN_OUT_AFTER_IN_MS;
    localStorage.setItem(SIGN_OUT_TARGET_STORAGE_KEY, newTargetTime.toString());
    return SIGN_OUT_AFTER_IN_MS;
  });

  useEffect(() => {
    if (!autoLogout) {
      return;
    }

    const intervalId = setInterval(() => {
      const targetStr = localStorage.getItem(SIGN_OUT_TARGET_STORAGE_KEY);
      if (!targetStr) {
        clearInterval(intervalId);
        return;
      }
      const targetTime = parseInt(targetStr, 10);
      const delta = targetTime - Date.now();

      if (delta <= 0) {
        clearInterval(intervalId);
        localStorage.removeItem(SIGN_OUT_TARGET_STORAGE_KEY);
        void signOut();
      } else {
        setRemainingTimeMs(delta);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [signOut, autoLogout]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: (theme) => theme.palette.background.default,
        zIndex: 9999,
        overflow: 'hidden',
        p: 2,
      }}
    >
      <img
        src={forbiddenAccessImage}
        style={{
          maxWidth: '100%',
          maxHeight: '60%',
          objectFit: 'contain',
          marginBottom: '2rem',
        }}
        alt="Forbidden Access"
      />
      <Typography
        variant="h5"
        textAlign="center"
        sx={{
          fontSize: { xs: '1rem', md: '1.5rem' },
          mb: 2,
          wordBreak: 'break-word',
          width: {
            xs: '100%',
            md: '80%',
            lg: '60%',
          },
        }}
      >
        {`Access to portal is forbidden because you don't have sufficient
        permissions to login. Contact ${orgName ?? 'your organization'} administrator if you think you should be able to access the portal.
        ${autoLogout ? `Logging you out in ${(remainingTimeMs / 1000).toFixed()} seconds.` : ''}`}
      </Typography>
      {autoLogout ? null : (
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => (window.location.href = '/')}
        >
          Go home
        </Button>
      )}
    </Box>
  );
}
